import * as Metadata from '../../entities/Metadata';
import { EntityType, entityTypeLabelMap } from '../../entities/common';
import { 
    PARENT_ENTITY_TYPES,
    getSubentitiesForEntity
} from './entitySubentityMapping';

// Helper function to create fields with proper interface compliance
const createField = (id: string, name: string, label: string, description: string, type: Metadata.FieldType, settings?: any): Metadata.Field => ({
    id,
    name,
    label,
    description,
    isNative: false,
    isCustom: false,
    isReadonly: false,
    isSystem: true,
    type,
    group: Metadata.FieldGroup.SystemFields,
    settings
});

// Build parent entity options (all top-level entity types)
const buildParentEntityOptions = () => {
    const options: Record<string, string> = {};
    
    // Iterate through all parent entity types in defined order
    PARENT_ENTITY_TYPES.forEach((key) => {
        options[key] = entityTypeLabelMap[key].singular;
    });
    
    return options;
};

// Create complete Field objects with all required properties
export const AuditLogsFields: Record<string, Metadata.Field> = {
    entityType: createField(
        'auditlog-entitytype',
        'entityType',
        'Entity Type',
        'Type of entity',
        Metadata.FieldType.Text,
        {
            editControl: 'Dropdown',
            options: buildParentEntityOptions()
        }
    ),

    eventType: createField(
        'auditlog-eventtype',
        'eventType',
        'Event Type',
        'Type of event',
        Metadata.FieldType.Text,
        {
            editControl: 'Dropdown',
            options: [
                { name: 'Created', value: 'Created' },
                { name: 'Updated', value: 'Updated' },
                { name: 'Deleted', value: 'Deleted' }
            ]
        }
    ),

    user: createField(
        'auditlog-user',
        'user',
        'Editor',
        'User who performed the action',
        Metadata.FieldType.User
    ),

    date: createField(
        'auditlog-date',
        'date',
        'Date',
        'Date of the event',
        Metadata.FieldType.Date
    )
};

/**
 * Create a dynamic subentity field based on the selected parent entity type(s)
 * This allows filtering subentities specific to the selected parent entity/entities
 * If multiple parent entities are selected, shows aggregated list of all possible subentities
 */
export function createSubentityFieldForEntity(parentEntityTypes: string | string[]): Metadata.Field {
    // Handle both single string and array of strings
    const entityTypesArray = Array.isArray(parentEntityTypes) ? parentEntityTypes : [parentEntityTypes];
    
    // Aggregate all subentities from all selected parent entities
    const allSubentities = new Set<string>();
    entityTypesArray.forEach(parentEntityType => {
        const subentities = getSubentitiesForEntity(parentEntityType);
        subentities.forEach(subentity => allSubentities.add(subentity));
    });
    
    // Build options for all aggregated subentities
    const options: Record<string, string> = {};
    
    // Add parent entity types as options (for filtering main entity only)
    entityTypesArray.forEach(parentEntityType => {
        const label = entityTypeLabelMap[parentEntityType as EntityType];
        if (label) {
            options[parentEntityType] = `${label.singular} Details`;
        }
    });
    
    // Add all subentity options
    Array.from(allSubentities).forEach(subentityType => {
        const label = entityTypeLabelMap[subentityType as EntityType];
        if (label) {
            options[subentityType] = label.singular;
        }
    });
    
    const description = entityTypesArray.length === 1 
        ? `Type of sub-entity for ${entityTypesArray[0]}`
        : `Type of sub-entity for selected entities`;
    
    return createField(
        'auditlog-subentitytype-filtered',
        'entityType',
        'Sub-Entity',
        description,
        Metadata.FieldType.Text,
        {
            editControl: 'Dropdown',
            options
        }
    );
}
